import SocketConnection from "./SocketConnection";
import { RouterClient } from "../../clients/routerClient";
import { System } from "../../common/system";
import { Logger } from "../../clients/logger";
import { WINDOW_SERVICE_REQUESTS } from "../../common/constants";
import AssimilationWindowManager from "./assimilationWindowManager";
import { WindowStartBounds } from "./types";
import { SocketConnectionStatus } from "./assimilationService";
import uuidv4 from "uuid-random";
import get from "lodash/get";
const { REMOTE_FOCUS } = WINDOW_SERVICE_REQUESTS;

const WS_CONNECT_FAILURE_TIMEOUT = 7500;
const SHIFT = 160;
const CTRL = 162;

const isWPF = (win: Record<string, any>) => get(win, "window.descriptor.isWPF", get(win, "descriptor.isWPF"));

type Message = Record<string, any>;
type Manifest = Record<string, any>;

export default class AssimilationSocketConnection extends SocketConnection {
	port: number;

	manifest: Record<string, any>;

	assimilationWindowManager: AssimilationWindowManager;

	keyboard: Record<string, any>;

	connectCallback?: null | ((sockedConnectionStatus: SocketConnectionStatus) => void);

	notifySocketConnectionFailure: boolean = false;

	constructor(manifest: Manifest, windowManager: AssimilationWindowManager) {
		super();
		this.port = manifest.port;
		this.manifest = manifest;
		this.assimilationWindowManager = windowManager;
		this.setupListeners();
		this.keyboard = {};
	}

	/**
	 *These are all of the socket listeners. All of these messages come in from Assimilation
	 *
	 * @memberof AssimilationSocketConnection
	 */
	setupListeners() {
		this.addListener("startMove", this.onWindowStartMove.bind(this));
		this.addListener("moveUpdate", this.onWindowMoveUpdate.bind(this));
		this.addListener("moveEnd", this.onMoveEnd.bind(this));
		this.addListener("windowClicked", this.onWindowClicked.bind(this));
		this.addListener("locationResponse", this.onLocationResponse.bind(this));
		this.addListener("addWindow", this.onAddWindow.bind(this));
		this.addListener("removeWindow", this.onRemoveWindow.bind(this));
		this.addListener("applicationList", this.onApplicationList.bind(this));
		this.addListener("keyDown", this.onKeyDown.bind(this));
		this.addListener("keyUp", this.onKeyUp.bind(this));
		this.addListener("focus", this.onWindowFocused.bind(this));
		this.addListener("maximize", this.onWindowMaximize.bind(this));
		this.addListener("restore", this.onWindowRestore.bind(this));
		this.addListener("minimize", this.onWindowMinimize.bind(this));
		this.addListener("openNewApp", this.onNewApp.bind(this));
		this.addListener("openAssetApp", this.onNewApp.bind(this));
		this.addListener("socketError", this.onSocketError.bind(this));
		this.addListener("socketClosed", this.onSocketClosed.bind(this));
		this.addListener("socketOpen", this.onSocketOpened.bind(this));
		this.addListener("closeWindow", this.onCloseWindow.bind(this)); // Assimilation responses the action done
		this.addListener("getBounds", this.onGetBoundsReturned.bind(this));
		this.addListener("hookWindow", this.onWindowHooked.bind(this));
		this.addListener("onLog", this.onLog.bind(this));
	}

	/**
	 * Responds to request for bounds from the system
	 * @param {*} msg
	 */
	onGetBoundsReturned(msg: Message) {
		Logger.system.verbose("APPLICATION LIFECYCLE:Assimilation:onGetBoundsReturned", msg.ref, msg);
		RouterClient.transmit(`${msg.ref}-Assimilation.boundsUpdate`, msg);
	}

	/**
	 * onWindowHooked is called once an application is successfully hooked with assimilation
	 * If the window requires setting alwaysOnTop, this will take care of making that call
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onWindowHooked(msg: Message) {
		const win = this.assimilationWindowManager.getWindowByUUID(msg.uuid);

		// If the window is found and alwaysOnTop is true, we need to call to assimilation
		// to make sure that state is honored
		if (win?.descriptor?.alwaysOnTop) {
			this.assimilationWindowManager.alwaysOnTop({
				data: {
					alwaysonTop: win.descriptor.alwaysOnTop,
					key: msg.uuid,
				},
			});
		}
	}

	/**
	 * onLog
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onLog(msg: Message) {
		type LogLevels = "info" | "warn" | "log" | "error" | "debug" | "verbose";
		let { type = "warn", content }: { type?: LogLevels; content?: string } = msg;

		if (typeof Logger.system[type] !== "function") {
			Logger.system.warn(`Assimilation unable to log type ${type} from EXE.  Defaulting to warn: ${content}`);
			return;
		}

		Logger.system[type](`Assimilation EXE: ${content}`);
	}

	/**
	 *onNewApp is called once an application has been spawned. This allows us to link the window handle with the information from the launcher service
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onNewApp(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:openNewApp", msg);
		// this only happens when using shell exec.
		if (msg.error && !msg.error.startsWith("The operation completed successfully"))
			return Logger.system.error(msg.error);
		try {
			const windowResponse = JSON.parse(msg.window);
			const windowObject = this.assimilationWindowManager.updateSpawningWindow(msg.ref, windowResponse.uuid);
			this.assimilationWindowManager.handleSpawnComplete(windowObject, windowResponse);
		} catch (err) {
			Logger.system.error("No Window found. Either the executable path is wrong or it has no window");
		}
	}

	onCloseWindow(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:closeWindow", msg);
	}

	/**
	 *This is called when a window is about to be moving. We store it's current location so we can compare on location updates
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowStartMove(msg: WindowStartBounds) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowStartMove", msg);
		if (!msg.uuid) {
			Logger.system.error("window is missing uuid", msg);
			return;
		}
		const movingWindow = this.assimilationWindowManager.setMovingWindow(
			this.assimilationWindowManager.getWindowByUUID(msg.uuid)
		);
		movingWindow.setStartBounds(msg);
	}

	/**
	 * This is fired on every move update. We just get the mouse x and y to reduce load on assimilation
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onWindowMoveUpdate(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowMoveUpdate", msg);
		const movingWindow = this.assimilationWindowManager.getMovingWindow();

		movingWindow?.calculateWindowLocation(msg);
		RouterClient.transmit(`AssimilationService.bounds-change-request.${movingWindow?.window.name}`, {
			...movingWindow?.location,
		});
	}

	/**
	 *
	 *This is fired when a window is maximized
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowMaximize(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowMaximize", msg);
		const { uuid } = msg.data ? JSON.parse(msg.data) : msg;
		const win = this.assimilationWindowManager.getWindowByUUID(uuid);
		if (!win) return;
		win.maximize({ fromAssimilation: true });
	}

	/**
	 *THis is fired when a window is minimized
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowMinimize(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowMinimize", msg);
		const { uuid } = msg.data ? JSON.parse(msg.data) : msg;
		const win = this.assimilationWindowManager.getWindowByUUID(uuid);
		if (!win) return;
		win.cachedLocation = win.location;
		win.minimize({ fromAssimilation: true });
	}

	/**
	 *This is fired when a window is restored
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowRestore(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowRestore", msg);
		const { uuid } = msg.data ? JSON.parse(msg.data) : msg;
		const win = this.assimilationWindowManager.getWindowByUUID(uuid);
		if (!win) {
			return;
		}
		// When the window is restored, we change the 'state' of the window. Without
		// this state change, we won't throw focus events.
		win.state = "restore";
		if (Date.now() - win.lastMinimized < this.assimilationWindowManager.assimilationHelper.manifest.eventIgnore) {
			// Minimizing a window causes the previous window in focus to get focused, which causes docking to restore this window. Ignore that restore.
			return;
		}
		win.restore({ fromAssimilation: true });
	}

	/**
	 * Every time assimilation detects an OS focus
	 * event (on any window, not just FSBL windows),
	 * it sends a message to this handler, containging
	 * the name, executable, uuid, and other information
	 * about the window being focused.
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowFocused(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowFocused", msg);
		const messageData = JSON.parse(msg.data);
		const win = this.assimilationWindowManager.getWindowByUUID(messageData.uuid);

		/** DH 6/15/2019
		 * Because Electron/OF and the OS maintain their own records
		 * of which window has focus, it's possible for the two to
		 * get out of sync. To prevent that, assimilation sends every
		 * focus event here, which forwards it to the window service
		 * to handle correctly.
		 *
		 * 6/25/2019 - messageData might not include the name FSBL has,
		 * so we prefer `win.name` if it's there. This speaks to a larger
		 * issue where Finsemble isn't aware of a window's handle, leading
		 * to ambiguitiy.
		 */
		const info = {
			name: (win && win.name) || messageData.name,
			windowHandle: messageData.uuid,
		};
		RouterClient.transmit(REMOTE_FOCUS, info);

		if (!win) return;
		setTimeout(() => {
			// delay focus events because focus happens simultaneously with minimize/restore which causes issues with docking groups.
			// if we have been recently restored or minimized skip the focus event.
			if (win.state === "minimize") return;
			if (Date.now() - win.lastRestored < this.assimilationWindowManager.assimilationHelper.manifest.eventIgnore)
				return;
			win.focus({ fromAssimilation: true });
		}, this.assimilationWindowManager.assimilationHelper.manifest.focusDelay);
	}
	/**
	 *onRemoveWindow is fired when a window is closed. This should only occur when the window is closed from the native application itself
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */

	onRemoveWindow(msg: Message) {
		const messageData = JSON.parse(msg.window);
		// Because we know this is never called on workspace close, we can set
		// removeFromWorkspace to true.
		this.assimilationWindowManager.removeWindow(messageData.uuid, true);
	}

	/**
	 *onWindowClicked is fired when a window is clicked and not moving
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onWindowClicked(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onWindowClicked", msg);
		const windowMessage = JSON.parse(msg.data);
		this.onWindowFocused(msg);
		const currentWindow = this.assimilationWindowManager.getWindowByUUID(windowMessage.uuid);
		/** DH 6/18/2019
		 * Under certain conditions,we found that we never receive a keyUp
		 * event for ctrl (namely when locking/unlocking the computer with
		 * different shortcuts). This was causing docking to go crazy with
		 * assimilated windows due to the below block. We've added the
		 * "not WPF" clause because, if a window is WPF, it has a title bar,
		 * and, ostensibly, doesn't need the ctrl+click shortcut. This doesn't
		 * fix the stuck ctrl key, but neither does it interfere with much else.
		 */
		if (!currentWindow || isWPF(currentWindow)) {
			Logger.system.debug(
				"APPLICATION LIFECYCLE:Assimilation:onWindowClicked no window found for window UUID",
				windowMessage.uuid
			);
			return;
		}

		if (this.keyboard[CTRL]) {
			// ctrl key is down No move here. Just group
			const dockingWindowName = currentWindow.parentWindow
				? currentWindow.parentWindow.windowName
				: currentWindow.windowName;
			RouterClient.query("DockingService.getGroupObjectsForWindow", { name: dockingWindowName }, (err, response) => {
				let grouped = false;
				for (let i = 0; i < response.data.length; i++) {
					const group = response.data[i];
					if (group.isMovable) {
						grouped = true;
						break;
					}
				}
				if (!grouped) {
					return RouterClient.transmit("DockingService.formGroup", {
						windowName: dockingWindowName,
					});
				}

				return RouterClient.query(
					"DockingService.leaveGroup",
					{
						name: dockingWindowName,
					},
					() => {}
				);
			});
		}
	}

	/**
	 *onApplicationList will receive a full list of running windows on the machine
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onApplicationList(msg: Message) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:onApplicationList", msg);
		this.assimilationWindowManager.parseApplicationList(msg.list);
	}

	/**
	 * This will get fired after a move to verify the window moved to the correct location
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onLocationResponse(msg: Message) {
		if (!msg.window) return;
		const windowMessage = JSON.parse(msg.window);
		const win = this.assimilationWindowManager.getWindowByUUID(windowMessage.uuid);
		if (!win) return;
		RouterClient.transmit("DockingService.updateWindowLocation", {
			windowName: win.windowName,
			location: {
				left: windowMessage.left,
				top: windowMessage.top,
				width: windowMessage.right - windowMessage.left,
				height: windowMessage.bottom - windowMessage.top,
			},
		});
	}

	/**
	 *when a window is created onAddWindow is fired and the window handle is added to a list
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onAddWindow(msg: Message) {
		const parsedWindow = JSON.parse(msg.window);
		if (!parsedWindow.uuid) return;
		Logger.system.debug("Assimilation.AssimilationWindowManager.onAddWindow invoking addNativeWindow:", parsedWindow);
		this.assimilationWindowManager.addNativeWindow(parsedWindow);
	}

	/**
	 *THis is fired when a move is complete so we can save the window location
	 *
	 * @param {*} msg
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	onMoveEnd(msg: Message) {
		Logger.system.info("moveEnd", msg);
		const currentMoving = this.assimilationWindowManager.getMovingWindow();
		if (!currentMoving) {
			return;
		}
		/** DH 6/18/2019
		 * Under certain conditions,we found that we never receive a keyUp
		 * event for ctrl (namely when locking/unlocking the computer with
		 * different shortcuts). This was causing docking to go crazy with
		 * assimilated windows due to the below block. We've added the
		 * "not WPF" clause because, if a window is WPF, it has a title bar,
		 * and, ostensibly, doesn't need the ctrl+click shortcut. This doesn't
		 * fix the stuck ctrl key, but neither does it interfere with much else.
		 */
		if (this.keyboard[CTRL] && !isWPF(currentMoving)) {
			// ctrl key is down No move here. Just group
			if (!currentMoving.window.grouped) {
				RouterClient.transmit("DockingService.formGroup", {
					windowName: currentMoving.window.windowName,
				});
			} else {
				return RouterClient.query(
					"DockingService.leaveGroup",
					{
						name: currentMoving.window.windowName,
					},
					() => {}
				);
			}
		}
		msg.location = currentMoving.window.location;
		RouterClient.transmit(`${currentMoving.window.name}.endMovement`, msg);
		this.assimilationWindowManager.saveWindow({
			data: {
				key: currentMoving.window.uuid,
				location: currentMoving.window.location,
			},
		});
	}

	/**
	 *This is for hot keys. We register every key up
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onKeyUp(msg: Message) {
		Logger.system.verbose("APPLICATION LIFECYCLE:Assimilation:onKeyUp", msg);
		msg.key = Number(msg.key);
		clearTimeout(this.keyboard[msg.key]);
		delete this.keyboard[msg.key];

		if (msg.key === SHIFT) {
			(self as any).shift = false;
		}
		RouterClient.transmit("finsemble.assimilation.keyup", { key: msg.key });
	}

	/**
	 *This is for hot keys. We register every key down
	 *
	 * @param {*} msg
	 * @memberof AssimilationSocketConnection
	 */
	onKeyDown(msg: Message) {
		Logger.system.verbose("APPLICATION LIFECYCLE:Assimilation:onKeyDown", msg);
		msg.key = Number(msg.key);
		if (this.keyboard[msg.key]) clearTimeout(this.keyboard[msg.key]);
		this.keyboard[msg.key] = setTimeout(() => this.onKeyUp(msg), this.manifest.hotkeyTimeout);

		if (msg.key === SHIFT) {
			(self as any).shift = true;
		}
		RouterClient.transmit("finsemble.assimilation.keydown", { key: msg.key });
	}

	/**
	 *Once we've connected to assimilation we need to do a few tasks
	 *
	 * @param {*} [cb=Function.prototype]
	 * @memberof AssimilationSocketConnection
	 */
	onSocketConnected(cb = Function.prototype) {
		this.enableAppHooks(); // Turn on the dll
		this.getMonitorInfo(); // send assimilation the monitor info
		if (this.connectCallback) {
			// when we connect we want to notify of any failure if we get an error or a disconnection
			this.notifySocketConnectionFailure = true;
			this.connectCallback({
				connected: true,
				error: null,
			});
			this.connectCallback = null;
		}
		cb();
	}

	/**
	 * On a socket error, log it and reconnect
	 *
	 * @memberof AssimilationSocketConnection
	 */
	onSocketError(err: Event) {
		Logger.system.log("APPLICATION LIFECYCLE:Assimilation:onSocketError", err);
		const timeout = setTimeout(() => {
			// Try to reconnect on error or disconnect
			clearTimeout(timeout);
			if (this.connectCallback && this.notifySocketConnectionFailure)
				this.connectCallback({
					error: err,
					connected: false,
				});
			// we don't want to keep notifying so we set this to false after a failure.
			this.notifySocketConnectionFailure = false;
			this.connect();
		}, 5000);
	}

	/**
	 *If a socket is closed, reconnect
	 *
	 * @memberof AssimilationSocketConnection
	 */
	onSocketClosed() {
		Logger.system.log("APPLICATION LIFECYCLE:Assimilation:onSocketClosed");
		this.connect();
	}

	/**
	 * we get this call when the socket connection is opened. We remove our timeout here.
	 */
	onSocketOpened() {
		Logger.system.log("APPLICATION LIFECYCLE:Assimilation:onSocketOpened");
		clearTimeout(this.wsConnectionFailureTimeout);
		this.onSocketConnected();
	}

	/**
	 *Set our throttle level for window move events
	 *
	 * @param {*} throttle
	 * @memberof AssimilationSocketConnection
	 */
	setThrottle(throttle: number) {
		Logger.system.log("APPLICATION LIFECYCLE:Assimilation:enable move throttle", throttle);
		this.send(
			this.prepareAction({
				topic: "throttle",
				throttle,
			})
		);
	}

	/**
	 *turn on the dll so we can catch window events
	 *
	 * @memberof AssimilationSocketConnection
	 */
	enableAppHooks() {
		Logger.system.info("APPLICATION LIFECYCLE:Assimilation:Enable Assimilation hooks");
		this.send(
			this.prepareAction({
				topic: "enableHooks",
				data: {
					whitelist: [], // coming soon
					blacklist: [], // coming soon
				},
			})
		);
	}

	// connect to the assimilation sockets
	connect(cb?: (sockedConnectionStatus: SocketConnectionStatus) => void) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:connect to websocket");
		if (cb) this.connectCallback = cb;
		if (this.ws && this.ws.readyState < 2) return; // No need to keep connecting if we're in the process
		if (!this.wsConnectionFailureTimeout) {
			this.wsConnectionFailureTimeout = window.setTimeout(() => {
				console.error(
					`APPLICATION LIFECYCLE: STARTUP: Assimilation service failed to connect to websocket within ${WS_CONNECT_FAILURE_TIMEOUT} milliseconds`
				);
				Logger.system.error(
					`APPLICATION LIFECYCLE: STARTUP: Assimilation service failed to connect to websocket within ${WS_CONNECT_FAILURE_TIMEOUT} milliseconds`
				);
				// we only want to notify once, which happened by inoking this callback above.
				this.notifySocketConnectionFailure = false;
				if (this.connectCallback) {
					this.connectCallback({
						connected: false,
						error: "Unable to connect to websocket",
					});
				}
			}, WS_CONNECT_FAILURE_TIMEOUT);
		}
		super.connect();
	}

	/**
	 *A helper function for send information to assimilation
	 *
	 * @param {*} json
	 * @returns
	 * @memberof AssimilationSocketConnection
	 */
	prepareAction(json: Message) {
		const ref = uuidv4();
		json.ref = ref;
		const output = JSON.stringify(json);

		json.expiry = Date.now() + 3000; // 3 seconds to wait
		return output;
	}

	/**
	 *Get the monitor info and send it to assimilation. We use this to calculate scaling.
	 *
	 * @memberof AssimilationSocketConnection
	 */
	getMonitorInfo() {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:getMonitorInfo");
		System.getMonitorInfo((monitorInfo) => {
			this.send(
				JSON.stringify({
					// Send our monitor info so we can move applications out of the claimed space.
					topic: "monitorInfo",
					data: monitorInfo,
				})
			);
		});
	}

	/**
	 *Send the updated monitor info to assimilation. We use this to calculate scaling.
	 *
	 * @memberof AssimilationSocketConnection
	 */
	setMonitorInfo(monitorInfo: Record<string, any>) {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:setMonitorInfo");
		this.send(
			JSON.stringify({
				// Send our monitor info so we can move applications out of the claimed space.
				topic: "monitorInfo",
				data: monitorInfo,
			})
		);
	}

	getApplicationList() {
		this.send(
			this.prepareAction({
				// send a request for a list of applications
				topic: "applicationList",
			})
		);
	}
}
